import { useState } from "react";
import { ChevronLeft, Search, X } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { type AccessLevelOption, type ShareGroup, type ShareUser } from "./types";

interface AccessDetailsPerson {
  email: string;
  label: string;
  avatarUrl?: string;
  effectiveRole: string;
  sources: Array<{ kind: "direct" } | { kind: "group"; groupName: string }>;
}

export const buildExpandedAccess = (
  users: ShareUser[],
  groups: ShareGroup[],
  accessLevels: Record<string, AccessLevelOption>
): AccessDetailsPerson[] => {
  const order = Object.keys(accessLevels);
  const rank = (role: string) => {
    const i = order.indexOf(role);
    return i === -1 ? 999 : i;
  };
  const byEmail = new Map<string, AccessDetailsPerson>();

  const add = (user: ShareUser, source: AccessDetailsPerson["sources"][number]) => {
    const existing = byEmail.get(user.email);
    if (!existing) {
      byEmail.set(user.email, {
        email: user.email,
        label: user.full_name || user.email,
        avatarUrl: user.image_url,
        effectiveRole: user.access_level ?? "",
        sources: [source],
      });
    } else {
      existing.sources.push(source);
      if (user.access_level && rank(user.access_level) < rank(existing.effectiveRole)) {
        existing.effectiveRole = user.access_level;
      }
    }
  };

  users.forEach((u) => add(u, { kind: "direct" }));
  groups.forEach((g) =>
    (g.users ?? []).forEach((u) => add(u, { kind: "group", groupName: g.display_name ?? g.name }))
  );

  return [...byEmail.values()].sort((a, b) => a.label.localeCompare(b.label));
};

interface AccessDetailsViewProps {
  resourceName: string;
  users: ShareUser[];
  groups: ShareGroup[];
  accessLevels: Record<string, AccessLevelOption>;
  currentUserEmail?: string | null;
  userImageMap?: Map<string, string | undefined>;
  onBack: () => void;
  onClose: () => void;
}

export const AccessDetailsView = ({
  resourceName,
  users,
  groups,
  accessLevels,
  currentUserEmail,
  userImageMap,
  onBack,
  onClose,
}: AccessDetailsViewProps) => {
  const [filterQuery, setFilterQuery] = useState("");

  const expanded = buildExpandedAccess(users, groups, accessLevels);
  const filtered = filterQuery.trim()
    ? expanded.filter(
        (p) =>
          p.label.toLowerCase().includes(filterQuery.toLowerCase()) ||
          p.email.toLowerCase().includes(filterQuery.toLowerCase())
      )
    : expanded;

  return (
    <>
      <div className="flex items-start gap-3 border-b border-border px-[22px] pb-3 pt-5">
        <button
          type="button"
          onClick={onBack}
          className="mt-[1px] flex size-[30px] shrink-0 cursor-pointer items-center justify-center rounded-md border border-border text-foreground hover:bg-muted"
        >
          <ChevronLeft className="size-4" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-xl font-semibold leading-tight tracking-tight text-foreground">
            All users with access
          </p>
          <p className="mt-1 truncate text-xs text-muted-foreground">
            {resourceName} · {expanded.length} {expanded.length === 1 ? "user" : "users"}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="mt-[-2px] flex size-[30px] shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
        >
          <X className="size-[18px]" />
        </button>
      </div>

      <div className="px-[22px] pb-1.5 pt-3">
        <div className="flex h-9 items-center gap-2 rounded-md border border-border bg-background px-2.5">
          <Search className="size-[15px] shrink-0 text-muted-foreground" />
          <input
            className="flex-1 border-none bg-transparent text-xs text-foreground outline-none placeholder:text-muted-foreground"
            placeholder="Filter by name or email"
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
          />
          {filterQuery && (
            <button
              type="button"
              onClick={() => setFilterQuery("")}
              className="cursor-pointer text-muted-foreground hover:text-foreground"
            >
              <X className="size-3" />
            </button>
          )}
        </div>
      </div>

      <div className="max-h-[420px] overflow-y-auto px-[22px] pb-4">
        {filtered.length === 0 ? (
          <p className="py-6 text-center text-xs text-muted-foreground">
            No users match &ldquo;{filterQuery}&rdquo;
          </p>
        ) : (
          filtered.map((person) => {
            const isSelf = person.email.toLowerCase() === currentUserEmail?.toLowerCase();
            const roleLabel = accessLevels[person.effectiveRole]?.label ?? person.effectiveRole;
            const groupNames = person.sources
              .filter((s): s is { kind: "group"; groupName: string } => s.kind === "group")
              .map((s) => s.groupName);
            return (
              <div key={person.email} className="flex items-center gap-3 py-2">
                <Avatar className="size-[34px] shrink-0">
                  <AvatarImage
                    src={
                      userImageMap?.get(person.email.toLowerCase()) || person.avatarUrl || undefined
                    }
                  />
                  <AvatarFallback className="text-xs">
                    {person.label.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate text-sm font-medium text-foreground">
                      {person.label}
                    </span>
                  </div>
                  <p className="truncate text-xs text-muted-foreground">
                    {person.email}
                    {groupNames.length > 0 && <> · via {groupNames.join(", ")}</>}
                  </p>
                </div>
                <span className="shrink-0 text-xs font-medium text-muted-foreground">
                  {roleLabel}
                </span>
              </div>
            );
          })
        )}
      </div>
    </>
  );
};
